// SPDX-FileCopyrightText: 2026 Stadtplatz.de
//
// SPDX-License-Identifier: EUPL-1.2

/**
 * Product Category Mappings (German translations)
 */
export const CATEGORY_MAP = {
  // Inventory Categories
  zelte_ueberdachungen: "Zelte & Überdachungen",
  moebel: "Möbel",
  buehnen_podeste: "Bühnen & Podeste",
  absperrungen_sicherheit: "Absperrungen & Sicherheit",
  technik: "Technik",
  marktstände: "Marktstände",
  dekoration_beschilderung: "Dekoration & Beschilderung",
  versorgung: "Versorgung",
  lagerung_transport: "Lagerung & Transport",
  sonstige_lagerartikel: "Sonstige Lagerartikel",

  // Service Categories
  aufbau_abbau_service: "Aufbau & Abbau Service",
  projektmanagement: "Projektmanagement",
  technische_services: "Technische Services",
  reinigung_wartung: "Reinigung & Wartung",
  sicherheit_personal: "Sicherheit & Personal",
  transport_logistik: "Transport & Logistik",
  beratung: "Beratung",
  notfall_services: "Notfall Services",
  individuelle_services: "Individuelle Services",
  sonstige_services: "Sonstige Services",

  // Fee Categories
  grundgebuehren: "Grundgebühren",
  servicegebuehren: "Servicegebühren",
  sondernutzungsgebuehren: "Sondernutzungsgebühren",
  genehmigungsgebuehren: "Genehmigungsgebühren",
  sonstige_gebuehren: "Sonstige Gebühren",

  // Generic
  andere: "Andere",
  other: "Sonstige",
  inventory: "Inventar",
  service: "Service",
};

/**
 * Product Type Mappings (German translations)
 */
export const PRODUCT_TYPE_MAP = {
  inventory: "Inventar",
  non_inventory: "Nicht-Inventar",
  service: "Service",
  delivery: "Lieferung",
  intern: "Intern",
  fee: "Gebühr",
  kit: "Kit",
  standard: "Standard",
  parking: "Parkplatz",
  radbox: "Radbox",
  infra: "Infrastruktur",
  none: "Keine",
};

/**
 * Status Mappings (Archiving Flags)
 */
export const STATUS_MAP = {
  active: {
    text: "Aktiv",
    class: "bg-green-100 text-green-800",
  },
  inactive: {
    text: "Inaktiv",
    class: "bg-yellow-100 text-yellow-800",
  },
  archived: {
    text: "Archiviert",
    class: "bg-purple-100 text-purple-800",
  },
  fordeletion: {
    text: "Zur Löschung",
    class: "bg-red-100 text-red-800",
  },
};

/**
 * Event Type Mappings
 */
export const EVENT_TYPE_MAP = {
  market: "Markt",
  culture: "Kultur",
  info: "Info",
  mobility: "Mobilität",
  charging: "Laden",
  delivery: "Lieferung",
  parking: "Parkplatz",
  radbox: "Radbox",
  infra: "Infrastruktur",
  none: "Keine",
};

/**
 * Event Size Mappings
 */
export const EVENT_SIZE_MAP = {
  none: "Keine",
  small: "Klein",
  medium: "Mittel",
  large: "Groß",
};

/**
 * Applicable For Mappings
 */
export const APPLICABLE_FOR_MAP = {
  internal: "Intern",
  external: "Extern",
  both: "Beide",
};

/**
 * Helper Functions
 */

/**
 * Get translated category name
 */
export const getCategoryName = (category) => {
  return CATEGORY_MAP[category] || category;
};

/**
 * Get translated product type name
 */
export const getProductTypeName = (type) => {
  return PRODUCT_TYPE_MAP[type] || type;
};

/**
 * Get status with styling
 */
export const getStatus = (status) => {
  return (
    STATUS_MAP[status] || {
      text: status,
      class: "bg-gray-100 text-gray-800",
    }
  );
};

/**
 * Get event type name
 */
export const getEventTypeName = (type) => {
  return EVENT_TYPE_MAP[type] || type;
};

/**
 * Get event size name
 */
export const getEventSizeName = (size) => {
  return EVENT_SIZE_MAP[size] || size;
};

/**
 * Get applicable for name
 */
export const getApplicableForName = (value) => {
  return APPLICABLE_FOR_MAP[value] || value;
};

/**
 * Convert mapping objects to dropdown options
 */
export const mapToOptions = (mapping) => {
  return Object.entries(mapping).map(([value, label]) => ({
    id: value,
    value,
    name: typeof label === "object" ? label.text : label,
    label: typeof label === "object" ? label.text : label,
  }));
};

/**
 * Get all category options
 */
export const getCategoryOptions = () => mapToOptions(CATEGORY_MAP);

/**
 * Get all product type options
 */
export const getProductTypeOptions = () => mapToOptions(PRODUCT_TYPE_MAP);

/**
 * Get all status options
 */
export const getStatusOptions = () => {
  return Object.entries(STATUS_MAP).map(([value, config]) => ({
    id: value,
    value,
    name: config.text,
    label: config.text,
    class: config.class,
  }));
};

/**
 * Get all event type options
 */
export const getEventTypeOptions = () => mapToOptions(EVENT_TYPE_MAP);

/**
 * Get all event size options
 */
export const getEventSizeOptions = () => mapToOptions(EVENT_SIZE_MAP);

/**
 * Get all applicable for options
 */
export const getApplicableForOptions = () => mapToOptions(APPLICABLE_FOR_MAP);

/**
 * Batch translate multiple categories
 */
export const translateCategories = (categories) => {
  if (!Array.isArray(categories)) return [];
  return categories.map((cat) => ({
    id: cat,
    name: getCategoryName(cat),
  }));
};

/**
 * Batch translate multiple product types
 */
export const translateProductTypes = (types) => {
  if (!Array.isArray(types)) return [];
  return types.map((type) => ({
    id: type,
    name: getProductTypeName(type),
  }));
};

/**
 * Batch translate statuses
 */
export const translateStatuses = (statuses) => {
  if (!Array.isArray(statuses)) return [];
  return statuses.map((status) => ({
    id: status,
    name: getStatus(status).text,
  }));
};
